import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { ChromeToggleFab } from './components/ChromeToggleFab';
import { BarcodeScanStep } from './components/BarcodeScanStep';
import { ProductInfoStep } from './components/ProductInfoStep';
import { PhotoCaptureStep } from './components/PhotoCaptureStep';
import { PhotoSelectStep } from './components/PhotoSelectStep';
import { resolveMockProduct, type MockProduct } from './helpers/mockProduct';
import { useMobileChromeGate } from './hooks/useMobileChromeGate';
import type { CapturedPhoto, CaptureStep } from './MobileProductCapture.types';
import './MobileProductCapture.scss';

const STEP_ORDER: CaptureStep[] = ['scan', 'product', 'photos', 'select'];

export const MobileProductCapture: FC = () => {
    const { t } = useTranslation();
    const { chromeHidden, toggleChrome } = useMobileChromeGate(true);

    const [step, setStep] = useState<CaptureStep>('scan');
    const [product, setProduct] = useState<MockProduct | null>(null);
    const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [savedCount, setSavedCount] = useState(0);
    const photosRef = useRef(photos);
    photosRef.current = photos;

    const stepIndex = STEP_ORDER.indexOf(step === 'success' ? 'select' : step);

    const revokePreviews = useCallback((items: CapturedPhoto[]) => {
        items.forEach((p) => URL.revokeObjectURL(p.preview));
    }, []);

    useEffect(() => {
        return () => {
            photosRef.current.forEach((p) => URL.revokeObjectURL(p.preview));
        };
    }, []);

    const handleScanned = useCallback((barcode: string) => {
        setProduct(resolveMockProduct(barcode));
        setStep('product');
    }, []);

    const goToScan = useCallback(() => setStep('scan'), []);
    const goToProduct = useCallback(() => {
        if (!product) return;
        setStep('product');
    }, [product]);
    const goToPhotos = useCallback(() => {
        if (!product) return;
        setStep('photos');
    }, [product]);
    const goToSelect = useCallback(() => {
        if (photos.length === 0) return;
        setSelectedIds((prev) => {
            if (prev.size > 0) return prev;
            return new Set(photos.map((p) => p.id));
        });
        setStep('select');
    }, [photos]);

    const handleAddPhotos = useCallback((files: FileList | null) => {
        if (!files?.length) return;
        const next: CapturedPhoto[] = Array.from(files)
            .filter((f) => f.type.startsWith('image/'))
            .map((file) => ({
                id: crypto.randomUUID(),
                file,
                preview: URL.createObjectURL(file),
                name: file.name,
            }));
        if (!next.length) return;
        setPhotos((prev) => [...prev, ...next]);
    }, []);

    const handleRemovePhoto = useCallback((id: string) => {
        setPhotos((prev) => {
            const target = prev.find((p) => p.id === id);
            if (target) URL.revokeObjectURL(target.preview);
            return prev.filter((p) => p.id !== id);
        });
        setSelectedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const handleToggleSelect = useCallback((id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedIds(new Set(photos.map((p) => p.id)));
    }, [photos]);

    const handleClearSelect = useCallback(() => {
        setSelectedIds(new Set());
    }, []);

    const handleSave = useCallback(() => {
        if (selectedIds.size === 0) return;
        setSavedCount(selectedIds.size);
        setStep('success');
    }, [selectedIds]);

    const handleReset = useCallback(() => {
        revokePreviews(photos);
        setPhotos([]);
        setSelectedIds(new Set());
        setProduct(null);
        setSavedCount(0);
        setStep('scan');
    }, [photos, revokePreviews]);

    const stepLabels = useMemo(
        () => [
            t('mobileProductCapture.steps.scan'),
            t('mobileProductCapture.steps.product'),
            t('mobileProductCapture.steps.photos'),
            t('mobileProductCapture.steps.select'),
        ],
        [t]
    );

    return (
        <div className="mpc-root">
            <ChromeToggleFab chromeHidden={chromeHidden} onToggle={toggleChrome} />

            <div className="mpc-shell">
                <header className="mpc-topbar">
                    <div>
                        <p className="mpc-topbar__eyebrow">{t('mobileProductCapture.demoBadge')}</p>
                        <h1>{t('mobileProductCapture.title')}</h1>
                    </div>
                    {step !== 'success' && (
                        <ol className="mpc-progress" aria-label="steps">
                            {stepLabels.map((label, index) => {
                                const unlocked =
                                    index === 0 ||
                                    (index === 1 && Boolean(product)) ||
                                    (index === 2 && Boolean(product)) ||
                                    (index === 3 && photos.length > 0);
                                const go =
                                    index === 0
                                        ? goToScan
                                        : index === 1
                                          ? goToProduct
                                          : index === 2
                                            ? goToPhotos
                                            : goToSelect;

                                return (
                                    <li
                                        key={label}
                                        className={
                                            index < stepIndex
                                                ? 'is-done'
                                                : index === stepIndex
                                                  ? 'is-active'
                                                  : undefined
                                        }
                                    >
                                        <button
                                            type="button"
                                            className="mpc-progress__btn"
                                            disabled={!unlocked || step === 'success'}
                                            onClick={go}
                                            aria-label={label}
                                        >
                                            <span className="mpc-progress__dot">{index + 1}</span>
                                            <span className="mpc-progress__label">{label}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </header>

                <main className="mpc-main">
                    {step === 'scan' && (
                        <BarcodeScanStep
                            active={step === 'scan'}
                            hasProduct={Boolean(product)}
                            onScanned={handleScanned}
                            onNext={goToProduct}
                        />
                    )}

                    {step === 'product' && product && (
                        <ProductInfoStep product={product} onBack={goToScan} onNext={goToPhotos} />
                    )}

                    {step === 'photos' && (
                        <PhotoCaptureStep
                            photos={photos}
                            onAddPhotos={handleAddPhotos}
                            onRemovePhoto={handleRemovePhoto}
                            onBack={goToProduct}
                            onNext={goToSelect}
                        />
                    )}

                    {step === 'select' && (
                        <PhotoSelectStep
                            photos={photos}
                            selectedIds={selectedIds}
                            onToggle={handleToggleSelect}
                            onSelectAll={handleSelectAll}
                            onClear={handleClearSelect}
                            onBack={goToPhotos}
                            onNext={handleSave}
                        />
                    )}

                    {step === 'success' && product && (
                        <div className="mpc-step mpc-step--success">
                            <header className="mpc-step__header">
                                <h2>{t('mobileProductCapture.success.title')}</h2>
                                <p>{t('mobileProductCapture.success.subtitle')}</p>
                            </header>
                            <article className="mpc-success-card mpc-success-card--fill">
                                <p>
                                    <span>{t('mobileProductCapture.success.barcode')}</span>
                                    <strong>{product.barcode}</strong>
                                </p>
                                <p>{t('mobileProductCapture.success.photoCount', { count: savedCount })}</p>
                            </article>
                            <div className="mpc-step-nav mpc-step-nav--single">
                                <Button
                                    type="button"
                                    variant="contained"
                                    className="mpc-btn-primary"
                                    onClick={handleReset}
                                >
                                    {t('mobileProductCapture.success.again')}
                                </Button>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};
